import React, { useState, useEffect, createContext } from "react";
import { Auth, Hub } from "aws-amplify";
import { CognitoUser } from "@aws-amplify/auth";
import { HubCallback } from "@aws-amplify/core/lib/Hub";
import * as apigw from "./apigw"

interface IAuthContext {
  currentUser: any;
  isAuthenticated: boolean;
  idToken: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  signIn(username: string): Promise<CognitoUser>;
  signOut(): Promise<any>;
  answerCustomChallenge(user: any, code: string): Promise<string>;
  updateUserAttributes(user: any, attributes: object): Promise<string>
  currentAuthenticatedUser(): Promise<any>
  setWechat(appid: string, secret: string): Promise<any>
  getWechat(opts?: any): Promise<any>
  setTwitter(appid: string): Promise<any>
  getTwitter(opts?: any): Promise<any>
  setInstagram(id: string, token: string): Promise<any>
  getInstagram(opts?: any): Promise<any>
}

export interface IWechat {
  appId: string,
  accessToken: string
}

export interface ITwitter {
  appName: string,
  accessToken: string
}

export interface IInstagram {
  id: string,
}

const signIn = (username: string): Promise<CognitoUser> => {
  return Auth.signIn(username);
};

const signOut = (): Promise<any> => {
  return Auth.signOut();
};

const answerCustomChallenge = (
  user: any,
  code: string
): Promise<string> => {
  return Auth.sendCustomChallengeAnswer(user, code);
};

const updateUserAttributes = (user: any, attributes: object): Promise<string> => {
  return Auth.updateUserAttributes(user, attributes)
};

const currentAuthenticatedUser = (): Promise<any> => {
  return Auth.currentAuthenticatedUser()
};

const setWechat = async (appid: string, secret: string): Promise<any> => {
  const res = await apigw.request<any>(
    "/system/set-wechat",
    { appid, secret }
  );
  return res;
};

const getTwitter = async (opts?: any): Promise<any> => {
  const res = await apigw.request<any>(
    "/system/get-twitter",
    {},
    opts
  );
  return res;
};

const setTwitter = async (appid: string): Promise<any> => {
  const res = await apigw.request<any>(
    "/system/set-twitter",
    { appid }
  );
  return res;
};

const getWechat = async (opts?: any): Promise<any> => {
  const res = await apigw.request<any>(
    "/system/get-wechat",
    {},
    opts
  );
  return res;
};

const setInstagram = async (id: string, token: string): Promise<any> => {
  const res = await apigw.request<any>(
    "/system/set-instagram",
    { id, token }
  );
  return res;
};

const getInstagram = async (opts?: any): Promise<any> => {
  const res = await apigw.request<any>(
    "/system/get-instagram",
    {},
    opts
  );
  return res;
};

const AuthContext = createContext<IAuthContext>({
  currentUser: null,
  isAuthenticated: false,
  idToken: null,
  accessToken: null,
  refreshToken: null,
  signIn,
  signOut,
  answerCustomChallenge,
  updateUserAttributes,
  currentAuthenticatedUser,
  setWechat,
  getWechat,
  setTwitter,
  getTwitter,
  setInstagram,
  getInstagram
});

const AuthProvider = (props: any) => {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const authListener: HubCallback = ({ payload: { event, data } }) => {
    switch (event) {
      case "signIn":
        Auth.currentUserInfo()
          .then(setCurrentUser)
          .catch(() => { });
        break;
      case "signOut":
        setCurrentUser(null);
        setIdToken(null);
        setAccessToken(null);
        setIdToken(null);
        break;
    }
  };

  useEffect(() => {
    setIsAuthenticated(currentUser !== null);
  }, [currentUser]);

  useEffect(() => {
    if (isLoaded) {
      props.onLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  useEffect(() => {
    if (isLoaded === false) {
      Auth.currentSession()
        .then((session) => {
          if (session && session.isValid()) {
            setIdToken(session.getIdToken().getJwtToken());
            setAccessToken(session.getAccessToken().getJwtToken());
            setRefreshToken(session.getRefreshToken().getToken());
            Auth.currentUserInfo()
              .then((user) => {
                setCurrentUser(user);
                setIsLoaded(true);
              })
              .catch(() => {
                setIsLoaded(true);
              });
          } else {
            setIsLoaded(true);
          }
        })
        .catch(() => {
          setIsLoaded(true);
        });
    }
  }, [isLoaded]);

  useEffect(() => {
    Hub.listen("auth", authListener);
    return () => Hub.remove("auth", authListener);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        currentUser: currentUser,
        isAuthenticated: isAuthenticated,
        idToken: idToken,
        accessToken: accessToken,
        refreshToken: refreshToken,
        signIn: signIn,
        signOut: signOut,
        answerCustomChallenge: answerCustomChallenge,
        updateUserAttributes: updateUserAttributes,
        currentAuthenticatedUser: currentAuthenticatedUser,
        setWechat: setWechat,
        getWechat: getWechat,
        setTwitter: setTwitter,
        getTwitter: getTwitter,
        setInstagram: setInstagram,
        getInstagram: getInstagram
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };